import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Archive, GitBranch } from 'lucide-react';
import { LoadingSpinner } from '../components/common';
import { Navbar } from '../components/Navbar';
import { AnalysisResultsView } from '../components/analysis/AnalysisResultsView';
import { vibeMapperAnalysis } from '../components/data/dummyData';
import { useAppContext } from '../contexts/useAppContext';
import { useSocket } from '../contexts/socketTypes';
import { AnalysisService } from '../services/analysisService';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { analytics } from '../services/analytics/posthog';
import type { RepositoryAnalysis } from '../components/types/analysis.types';
import Footer from '../components/Footer';

export const DemoAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const { selectedRepository, analysisData, isDemoMode, setDemoMode } = useAppContext();
  const { socket, isConnected, joinJob, leaveJob } = useSocket();
  const { isAuthenticated, isInitialized } = useJWTAuth();

  // Redirect unauthenticated non-demo users to sign in
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isDemoMode) {
      sessionStorage.setItem('auth_return_url', window.location.pathname);
      navigate('/signin', { replace: true });
    }
  }, [isInitialized, isAuthenticated, isDemoMode, navigate]);

  const [analysis, setAnalysis] = useState<RepositoryAnalysis | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisStartTime] = useState<number>(Date.now());

  // Join the job-specific room so we actually receive socket events for this analysis
  useEffect(() => {
    if (!jobId || !isConnected) return;

    joinJob(jobId);
    return () => leaveJob(jobId);
  }, [jobId, isConnected, joinJob, leaveJob]);

  // Handle analysis loading
  useEffect(() => {
    if (!jobId) {
      if (isDemoMode) {
        setLoading(false);
        return;
      }
      if (!selectedRepository || !analysisData) {
        navigate('/repositories');
      }
      return;
    }

    if (isDemoMode) {
      setAnalysis(vibeMapperAnalysis);
      setLoading(false);
      return;
    }

    let mounted = true;

    const refetchFromApi = async () => {
      const res = await AnalysisService.getJobStatus(jobId);
      if (!mounted) return false;

      if (res?.data?.status === 'completed' && res?.data?.result) {
        setAnalysis(res.data.result);
        setLoading(false);
        if (selectedRepository) {
          analytics.analysisCompleted(
            selectedRepository.url || selectedRepository.name,
            Date.now() - analysisStartTime,
          );
        }
        return true;
      }

      if (res?.data?.status === 'failed') {
        setError(res.data.error || 'Analysis failed');
        setLoading(false);
        if (selectedRepository) {
          analytics.analysisFailed(
            selectedRepository.url || selectedRepository.name,
            res.data.error || 'Unknown error',
          );
        }
        return true;
      }

      return false;
    };

    const getOrFetchResults = async () => {
      try {
        await refetchFromApi();
        // If still running, keep loading state - socket will update when complete
      } catch (e: unknown) {
        if (mounted) {
          setError(e instanceof Error ? e.message : 'Failed to load analysis results');
          setLoading(false);
        }
      }
    };
    getOrFetchResults();

    const onComplete = async (data: { jobId?: string; analysis?: RepositoryAnalysis }) => {
      if (!mounted) return;
      if (data?.jobId !== jobId) return;

      if (data?.analysis) {
        setAnalysis(data.analysis);
        setLoading(false);
        if (selectedRepository) {
          analytics.analysisCompleted(
            selectedRepository.url || selectedRepository.name,
            Date.now() - analysisStartTime,
          );
        }
        return;
      }

      // Fallback when server emits terminal event without analysis payload
      try {
        await refetchFromApi();
      } catch (e: unknown) {
        if (mounted) {
          setError(e instanceof Error ? e.message : 'Failed to load analysis results');
          setLoading(false);
        }
      }
    };
    const onError = (data: { jobId?: string; error?: string }) => {
      if (!mounted) return;
      if (data?.jobId !== jobId) return;
      setError(String(data?.error || 'Analysis failed'));
      setLoading(false);
      if (selectedRepository) {
        analytics.analysisFailed(
          selectedRepository.url || selectedRepository.name,
          String(data?.error || 'Unknown error'),
        );
      }
    };

    socket?.on('analysis-complete', onComplete);
    socket?.on('analysis-error', onError);
    socket?.on('analysis-failed', onError);

    return () => {
      mounted = false;
      socket?.off('analysis-complete', onComplete);
      socket?.off('analysis-error', onError);
      socket?.off('analysis-failed', onError);
    };
  }, [jobId, socket, selectedRepository, analysisData, isDemoMode, navigate, analysisStartTime]);

  const displayRepository =
    jobId || isDemoMode
      ? {
          id: jobId || 'demo',
          name: selectedRepository?.name || 'demo-project',
          description: 'Demo repository for analysis',
          updatedAt: new Date().toISOString(),
          language: 'TypeScript',
          private: true,
          isPrivate: true,
          url: 'https://github.com/demo/demo-project',
          fullName: 'demo/demo-project',
        }
      : selectedRepository;

  if (jobId && loading) {
    return (
      <div className="min-h-screen">
        <Navbar pageTitle="Repository Findings" showDivider={true} />
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p style={{ color: 'var(--color-text-secondary)' }}>Loading analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar pageTitle="Repository Findings" showDivider={true} />
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Link
              to="/repositories"
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Back to Repositories
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleBackClick = () => {
    if (isDemoMode) {
      setDemoMode(false);
      navigate('/');
    } else {
      navigate('/dashboard');
    }
  };

  const isDemo = isDemoMode;
  const resultsData = analysis || vibeMapperAnalysis;
  const isUploadSource = selectedRepository?.source === 'upload';
  const SourceIcon = isUploadSource ? Archive : GitBranch;
  const repoName = resultsData?.repositoryName || displayRepository?.name || 'Repository';

  return (
    <div className="min-h-screen transition-colors duration-200">
      <Navbar
        pageTitle="Repository Findings"
        showDivider={true}
        leftContent={
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <SourceIcon className="w-4 h-4" />
            <span>{repoName}</span>
            {isDemo && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                Demo
              </span>
            )}
          </div>
        }
      />

      <main className="pt-(--navbar-offset)">
        <AnalysisResultsView
          key={analysis ? 'real-analysis' : 'demo-analysis'}
          data={resultsData}
          onBackToRepository={handleBackClick}
        />
      </main>
      <Footer />
    </div>
  );
};
